import { useNavigate } from 'react-router-dom';
import { useTeacher } from '@/context/TeacherContext';
import { FileText, Sparkles, ArrowLeft, Calendar } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn, getButtonColor } from '@/lib/utils';

export default function TeacherAITools() {
  const navigate = useNavigate();
  const { teacherTheme, isDark } = useTeacher();

  const tools = [
    {
      id: 'question-generator',
      title: 'Question Generator',
      description: 'Generate AI-powered questions automatically based on your curriculum, difficulty level, and question types. Create MCQ, True/False, Short Answer, and Written Detailed questions.',
      icon: FileText,
      color: 'from-blue-500 to-cyan-500',
      hoverColor: 'hover:from-blue-600 hover:to-cyan-600',
      iconBg: 'bg-blue-500/10',
      iconColor: 'text-blue-600',
      darkIconColor: 'dark:text-blue-400'
    },
    {
      id: 'chapter-planner',
      title: 'Chapter Planner',
      description: 'Generate AI-powered period-wise teaching plans with lesson scripts, student notes, and automatic adjustment based on classroom progress.',
      icon: Calendar,
      color: 'from-green-500 to-emerald-500',
      hoverColor: 'hover:from-green-600 hover:to-emerald-600',
      iconBg: 'bg-green-500/10',
      iconColor: 'text-green-600',
      darkIconColor: 'dark:text-green-400'
    }
  ];

  const handleToolClick = (toolId) => {
    if (toolId === 'question-generator') {
      navigate('/teacher/ai-tools/question-generator');
    } else if (toolId === 'class-note-maker') {
      navigate('/teacher/notes');
    } else if (toolId === 'chapter-planner') {
      navigate('/teacher/ai-tools/chapter-planner');
    }
  };

  return (
    <div className={cn("flex flex-col h-full p-6 overflow-y-auto", isDark ? "bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--card))] to-[hsl(var(--background))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]")}>
      <div className="max-w-7xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/teacher/dashboard')}
              className={cn("rounded-xl", isDark ? "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/60" : "text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EBE3]/60")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className={cn("text-4xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>AI Tools</h1>
              <p className={cn("mt-1 text-sm font-normal", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#8B7B6B]")}>Leverage AI to enhance your teaching workflow</p>
            </div>
          </div>
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <Card
                key={tool.id}
                onClick={() => handleToolClick(tool.id)}
                className={cn(
                  "cursor-pointer transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl",
                  isDark 
                    ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] hover:border-[hsl(var(--primary))]" 
                    : "bg-white border-[#E0D4BF] hover:border-[#C9A961]",
                  "shadow-md"
                )}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={cn(
                      "flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center",
                      tool.iconBg,
                      isDark ? tool.darkIconColor : tool.iconColor
                    )}>
                      <Icon className="w-7 h-7" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className={cn(
                        "text-xl font-bold mb-2",
                        isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]"
                      )}>
                        {tool.title}
                      </h3>
                      <p className={cn(
                        "text-sm leading-relaxed",
                        isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]"
                      )}>
                        {tool.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Action Indicator */}
                  <div className="mt-4 flex items-center justify-end">
                    <div className={cn(
                      "flex items-center gap-2 text-sm font-medium",
                      isDark ? "text-[hsl(var(--primary))]" : "text-[#C9A961]"
                    )}>
                      <span>Open Tool</span>
                      <ArrowLeft className="w-4 h-4 rotate-180" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Coming Soon Section */}
        <div className="mt-8">
          <h2 className={cn(
            "text-2xl font-bold mb-4",
            isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]"
          )}>
            More Tools Coming Soon
          </h2>
          <p className={cn(
            "text-sm",
            isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]"
          )}>
            We're continuously adding new AI-powered tools to help you enhance your teaching experience.
          </p>
        </div>
      </div>
    </div>
  );
}
